/**
 * MapAttribution.tsx
 * ─────────────────────────────────────────────────────────────────────────────
 * سطر إسناد المصادر — **التزام رخصي لا لمسة تجميلية**.
 *
 * الخريطة كانت تُركَّب بـ`attributionControl={false}` بلا أي بديل، أي بلا إسناد
 * إطلاقاً. وذلك يخالف شروطاً صريحة لا اختيارية:
 *
 *   • **OpenStreetMap** — رخصة ODbL تشترط نسبة البيانات إلى مساهميها.
 *   • **CARTO** — شروط خرائط الأساس المجّانية (`basemaps.cartocdn.com`) تشترط
 *     ظهور اسمها مع OSM.
 *   • **CIRA/RAMMB (‏SLIDER)** — يطلب الإسناد صراحةً على صفحته.
 *   • **EUMETSAT** — صور Meteosat «بيانات مُعدَّلة» تستوجب الذكر، وSLIDER نفسه
 *     يكتب ذلك على صفحته.
 *   • **ECMWF** — البيانات المفتوحة تحت CC-BY-4.0، و«BY» تعني الإسناد.
 *   • **DWD** — بيانات ICON المفتوحة مشروطة بذكر المصدر.
 *   • **GeoNames** — أسماء الأماكن العالمية (‏cities500 وalternateNamesV2) تحت
 *     CC-BY-4.0، و«BY» تعني الإسناد. راجع `scripts/build_place_shards.py`.
 *
 * أمّا NOAA/NCEP وNASA FIRMS فمُنتَجات حكومة أمريكية في الملك العام: ذكرها أمانةٌ
 * علمية لا إلزام. أُدرجت لأن إخفاء مصدر رقم أرصادي يجعله بلا مرجع يُحاكَم عليه.
 *
 * ── لماذا مطويّ بشكل افتراضي ─────────────────────────────────────────────────
 * الإسناد يجب أن يكون **ظاهراً وقابلاً للوصول**، لا أن يحتلّ الخريطة. زرٌّ صغير
 * دائم الظهور يفتح القائمة الكاملة هو ما تفعله الخرائط الجادّة (‏Google، Mapbox،
 * Zoom Earth) — يفي بالشرط بلا أن يزاحم المحتوى.
 */
export interface MapCredit {
    /** اسم الجهة كما تطلب أن تُذكر. لا يُترجَم — أسماء أعلام. */
    name: string;
    href: string;
    /** ما تُسهم به، بمفتاح ترجمة. */
    forKey: string;
}

/* الترتيب: خريطة الأساس أوّلاً (شرط الرخصة الأوضح)، ثم النماذج، ثم بقيّة الطبقات. */
export const MAP_CREDITS: MapCredit[] = [
    { name: 'OpenStreetMap', href: 'https://www.openstreetmap.org/copyright', forKey: 'attrib.basemap' },
    { name: 'CARTO', href: 'https://carto.com/attributions', forKey: 'attrib.basemap' },
    { name: 'NOAA / NCEP', href: 'https://www.emc.ncep.noaa.gov/emc/pages/numerical_forecast_systems/gfs.php', forKey: 'attrib.gfs' },
    { name: 'ECMWF', href: 'https://www.ecmwf.int/en/forecasts/datasets/open-data', forKey: 'attrib.ecmwf' },
    { name: 'DWD', href: 'https://www.dwd.de/EN/ourservices/opendata/opendata.html', forKey: 'attrib.icon' },
    { name: 'CIRA / RAMMB, CSU', href: 'https://slider.cira.colostate.edu/', forKey: 'attrib.satellite' },
    { name: 'EUMETSAT', href: 'https://www.eumetsat.int/', forKey: 'attrib.meteosat' },
    // البروق المرصودة. إسناد EUMETSAT **شرطٌ** في رخصة CC-BY-4.0 للمنتج (سياسة البيانات،
    // المادّة 6.3) لا مجاملة: حذفه يُخرج الاستعمال من الرخصة.
    { name: 'NOAA MRMS', href: 'https://www.nssl.noaa.gov/projects/mrms/', forKey: 'attrib.radar' },
    // ‏OPERA بترخيص CC BY 4.0: الإسناد شرطٌ في الرخصة لا مجاملة.
    { name: 'EUMETNET OPERA', href: 'https://www.eumetnet.eu/observations/opera-radar-animation/', forKey: 'attrib.radar' },
    // رادار كندا: رخصة ECCC تشترط «Data Source: Environment and Climate Change Canada».
    { name: 'Data Source: Environment and Climate Change Canada', href: 'https://eccc-msc.github.io/open-data/licence/readme_en/', forKey: 'attrib.radar' },
    // رادار إيطاليا: CC BY-SA 4.0 — ذكر «Radar-DPC» شرط، والصور المشتقّة بنفس الرخصة.
    { name: 'Radar-DPC (CC BY-SA 4.0)', href: 'https://radar.protezionecivile.it/', forKey: 'attrib.radar' },
    // رادار تايوان: OGDL-Taiwan-1.0 — «يُذكر المصدر في كل استعمال» شرطُ الرخصة.
    { name: 'CWA Taiwan (OGDL 1.0)', href: 'https://data.gov.tw/en/datasets/75125', forKey: 'attrib.radar' },
    /*
     * ── البروق: سطرٌ واحد لا سطران (طلب العميل 2026-09-17) ────────────────────
     * طلب إخفاء مصدر البروق كي لا يُستنسخ. ما أمكن تنفيذه منه نُفِّذ، وما لا يُنفَّذ
     * يُفصح عنه هنا كي لا يُحذف لاحقاً بحسن نيّة:
     *
     *   • **حُذف سطر NOAA GOES GLM**: منتَج حكومة أمريكية في الملك العام، فذكره
     *     أمانةٌ علمية لا إلزام — وله أن يسقط بقرار المالك.
     *   • **بقي سطر EUMETSAT**: نصّه شرطٌ حرفيّ في رخصة المنتج (سياسة البيانات،
     *     المادّة 6.3). حذفه لا «يُخفي» المصدر بل **يُخرج استعمالنا من الرخصة**،
     *     وهو خطرٌ أثقل بما لا يُقاس من الاستنساخ الذي يُتّقى به.
     *
     * والرابط صار صفحة الترخيص العامّة بدل صفحة مِجهار البروق: الشرط يقع على
     * **النصّ** لا على الوجهة، فلا نُعلن الأداة بعينها بلا داعٍ رخصي.
     */
    { name: 'Contains modified EUMETSAT Meteosat data', href: 'https://www.eumetsat.int/eumetsat-data-licensing', forKey: 'attrib.lightning' },
    { name: 'GDACS', href: 'https://www.gdacs.org/', forKey: 'attrib.cyclones' },
    { name: 'NASA FIRMS', href: 'https://firms.modaps.eosdis.nasa.gov/', forKey: 'attrib.wildfires' },
    { name: 'Open-Meteo', href: 'https://open-meteo.com/', forKey: 'attrib.point' },
    { name: 'GeoNames', href: 'https://www.geonames.org/', forKey: 'attrib.places' },
];
